import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Search,
    Mail,
    User,
    Loader2,
    Send,
    CheckCircle,
    Plus,
    X
} from 'lucide-react';
import { toast } from 'sonner';

const REQUEST_TYPE_OPTIONS = [
    { value: 'full_song', label: 'Full Song' },
    { value: 'jingle', label: 'Jingle' },
    { value: 'ghostwrite', label: 'Ghostwrite' },
    { value: 'custom', label: 'Custom' }
];

export function InviteCreatorModal({ 
    open, 
    onOpenChange, 
    projectId,
    projectTitle,
    projects = [], // For when selecting from multiple projects
    onSuccess 
}) {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('platform');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
    
    // Email invite fields
    const [email, setEmail] = useState('');
    
    // Common fields
    const [message, setMessage] = useState('');
    const [requestType, setRequestType] = useState('');
    const [deadlineAt, setDeadlineAt] = useState('');
    const [budgetRange, setBudgetRange] = useState('');
    const [referenceLinks, setReferenceLinks] = useState([]);
    
    const [sending, setSending] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        if (projectId) {
            setSelectedProjectId(projectId);
        }
    }, [projectId]);

    useEffect(() => {
        if (searchQuery.length >= 2) {
            searchSubmitters();
        } else {
            setSearchResults([]);
        }
    }, [searchQuery]);

    const searchSubmitters = async () => {
        setSearching(true);
        try {
            const response = await fetch(`${API}/users/submitters?q=${encodeURIComponent(searchQuery)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setSearching(false);
        }
    };

    const handleSendInvite = async () => {
        if (!selectedProjectId) {
            toast.error('Please select a project');
            return;
        }

        if (activeTab === 'platform' && !selectedUser) {
            toast.error('Please select a user to invite');
            return;
        }

        if (activeTab === 'email' && !email) {
            toast.error('Please enter an email address');
            return;
        }

        setSending(true);
        try {
            const payload = {
                project_id: selectedProjectId,
                message: message || null,
                request_type: requestType || null,
                deadline_at: deadlineAt || null,
                budget_range: budgetRange || null,
                reference_links: referenceLinks.length > 0 ? referenceLinks : null
            };

            if (activeTab === 'platform') {
                payload.submitter_user_id = selectedUser.id;
            } else {
                payload.email = email;
            }

            const response = await fetch(`${API}/poster/invites`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(`Invite sent to ${activeTab === 'platform' ? selectedUser.email : email}!`);
                resetForm();
                onOpenChange(false);
                if (onSuccess) onSuccess(data);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to send invite');
            }
        } catch (error) {
            toast.error('Error sending invite');
        } finally {
            setSending(false);
        }
    };

    const resetForm = () => {
        setSearchQuery('');
        setSearchResults([]);
        setSelectedUser(null);
        setEmail('');
        setMessage('');
        setRequestType('');
        setDeadlineAt('');
        setBudgetRange('');
        setReferenceLinks([]);
    };

    const addReferenceLink = () => {
        setReferenceLinks([...referenceLinks, { text: '', href: '' }]);
    };

    const updateReferenceLink = (index, field, value) => {
        const updated = [...referenceLinks];
        updated[index][field] = value;
        setReferenceLinks(updated);
    };

    const removeReferenceLink = (index) => {
        setReferenceLinks(referenceLinks.filter((_, i) => i !== index));
    };

    const currentProjectTitle = projectTitle || projects.find(p => p.id === selectedProjectId)?.title || '';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#1A1A1A] border-[#333] max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Send className="w-5 h-5 text-[#F51E1E]" />
                        Invite Creator
                    </DialogTitle>
                    <DialogDescription className="text-[#A3A3A3]">
                        Invite a music creator to submit to your project
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Project Selection (if multiple projects available) */}
                    {!projectId && projects.length > 0 && (
                        <div>
                            <Label className="text-white mb-2 block">Select Project *</Label>
                            <select
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                                className="w-full h-10 px-3 rounded-md bg-[#262626] border border-[#404040] text-white focus:outline-none focus:ring-2 focus:ring-[#F51E1E]"
                            >
                                <option value="" disabled>Choose a project...</option>
                                {projects.filter(p => p.status === 'open').map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {currentProjectTitle && (
                        <div className="bg-[#262626] rounded-lg p-3">
                            <p className="text-sm text-[#A3A3A3]">Inviting to:</p>
                            <p className="text-white font-medium">{currentProjectTitle}</p>
                        </div>
                    )}

                    {/* Invite Method Tabs */}
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full bg-[#262626] p-1">
                            <TabsTrigger 
                                value="platform" 
                                className="flex-1 data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white"
                            >
                                <User className="w-4 h-4 mr-2" />
                                On Pitch Room
                            </TabsTrigger>
                            <TabsTrigger 
                                value="email"
                                className="flex-1 data-[state=active]:bg-[#F51E1E] data-[state=active]:text-white"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                By Email
                            </TabsTrigger>
                        </TabsList>

                        {/* Platform User Search */}
                        <TabsContent value="platform" className="mt-4 space-y-4">
                            <div>
                                <Label className="text-white mb-2 block">Search Artists/Producers</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                    <Input
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search by name or email..."
                                        className="pl-10 bg-[#262626] border-[#404040] text-white"
                                    />
                                </div>
                            </div>

                            {/* Selected User */}
                            {selectedUser && (
                                <div className="bg-[#F51E1E]/10 border border-[#F51E1E]/30 rounded-lg p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-[#F51E1E]" />
                                        <div>
                                            <p className="text-white font-medium">
                                                {selectedUser.first_name} {selectedUser.last_name}
                                            </p>
                                            <p className="text-sm text-[#A3A3A3]">{selectedUser.email}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedUser(null)}
                                        className="text-[#A3A3A3] hover:text-white"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}

                            {/* Search Results */}
                            {!selectedUser && searchQuery.length >= 2 && (
                                <div className="bg-[#262626] rounded-lg border border-[#404040] max-h-48 overflow-y-auto">
                                    {searching ? (
                                        <div className="flex items-center justify-center py-6">
                                            <Loader2 className="w-5 h-5 animate-spin text-[#F51E1E]" />
                                        </div>
                                    ) : searchResults.length === 0 ? (
                                        <div className="text-center py-6 text-[#666]">
                                            No artists found
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-[#404040]">
                                            {searchResults.map((user) => (
                                                <button
                                                    key={user.id}
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setSearchQuery('');
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-[#333] transition-colors flex items-center gap-3"
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-[#404040] flex items-center justify-center">
                                                        <User className="w-4 h-4 text-[#888]" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white text-sm font-medium">
                                                            {user.first_name} {user.last_name}
                                                        </p>
                                                        <p className="text-xs text-[#666]">{user.email}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </TabsContent>

                        {/* Email Invite */}
                        <TabsContent value="email" className="mt-4 space-y-4">
                            <div>
                                <Label className="text-white mb-2 block">Email Address *</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="artist@example.com"
                                    className="bg-[#262626] border-[#404040] text-white"
                                />
                            </div>
                        </TabsContent>
                    </Tabs>

                    {/* Common Fields */}
                    <div className="border-t border-[#333] pt-4 space-y-4">
                        <h3 className="text-sm font-medium text-[#A3A3A3]">Invite Details (Optional)</h3>

                        {/* Message */}
                        <div>
                            <Label className="text-white mb-2 block">Personal Message</Label>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Tell them why you'd like them to submit..."
                                className="bg-[#262626] border-[#404040] text-white resize-none"
                                rows={3}
                            />
                        </div>

                        {/* Request Type */}
                        <div>
                            <Label className="text-white mb-2 block">Request Type</Label>
                            <select
                                value={requestType}
                                onChange={(e) => setRequestType(e.target.value)}
                                className="w-full h-10 px-3 rounded-md bg-[#262626] border border-[#404040] text-white focus:outline-none focus:ring-2 focus:ring-[#F51E1E]"
                            >
                                <option value="">Not specified</option>
                                {REQUEST_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Deadline & Budget Row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white mb-2 block">Deadline</Label>
                                <Input
                                    type="date"
                                    value={deadlineAt ? deadlineAt.split('T')[0] : ''}
                                    onChange={(e) => setDeadlineAt(e.target.value ? `${e.target.value}T00:00:00Z` : '')}
                                    className="bg-[#262626] border-[#404040] text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-white mb-2 block">Budget Range</Label>
                                <Input
                                    value={budgetRange}
                                    onChange={(e) => setBudgetRange(e.target.value)}
                                    placeholder="e.g., $500-$1000"
                                    className="bg-[#262626] border-[#404040] text-white"
                                />
                            </div>
                        </div>

                        {/* Reference Links */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-white">Reference Links</Label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={addReferenceLink}
                                    className="text-[#F51E1E] hover:text-[#F51E1E]/80"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Link
                                </Button>
                            </div>
                            {referenceLinks.map((link, idx) => (
                                <div key={idx} className="flex gap-2 mb-2">
                                    <Input
                                        value={link.text}
                                        onChange={(e) => updateReferenceLink(idx, 'text', e.target.value)}
                                        placeholder="Label"
                                        className="flex-1 bg-[#262626] border-[#404040] text-white"
                                    />
                                    <Input
                                        value={link.href}
                                        onChange={(e) => updateReferenceLink(idx, 'href', e.target.value)}
                                        placeholder="URL"
                                        className="flex-[2] bg-[#262626] border-[#404040] text-white"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeReferenceLink(idx)}
                                        className="text-[#666] hover:text-red-400"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="border-[#404040] text-white hover:bg-[#262626]"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendInvite}
                        disabled={sending || !selectedProjectId || (activeTab === 'platform' && !selectedUser) || (activeTab === 'email' && !email)}
                        className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                    >
                        {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Invite
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
